import React from 'react';
import { Badge, Box, Flex, Group, Paper, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconTrendingUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Eyebrow, NumberMono } from './primitives';
import { CompetencyRow } from './types';
import { scoreExpectation } from './score';

const MAX_SCORE = 5;

/** Дорожка оценки. Общая у строк компетенций и у итога — отличается только
 *  то, что стоит над ней. */
const ScoreTrack: React.FC<{ score: number; color: string }> = ({ score, color }) => (
  <Box
    style={{
      position: 'relative',
      height: 8,
      background: 'var(--mantine-color-default-border)',
      borderRadius: 999,
      overflow: 'hidden',
    }}
  >
    <Box
      style={{
        position: 'absolute',
        inset: 0,
        width: `${(score / MAX_SCORE) * 100}%`,
        background: `var(--mantine-color-${color}-filled)`,
        borderRadius: 999,
        transition: 'width 600ms cubic-bezier(.2,.8,.2,1)',
      }}
    />
  </Box>
);

const CompetencyBar: React.FC<{ c: CompetencyRow; labels?: string[] }> = ({ c, labels }) => {
  // Same verdict badge as the editor / published summary / results — the score
  // never reads one way here and another way there.
  const meta = scoreExpectation(c.score, labels);
  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} gap="sm">
        <Text size="sm" fw={500}>
          {c.name}
        </Text>
        <Badge
          variant="light"
          color={meta.color}
          radius="sm"
          size="md"
          style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
        >
          {c.score.toFixed(1)}/{MAX_SCORE}
          {meta.label ? ` · ${meta.label}` : ''}
        </Badge>
      </Flex>
      <ScoreTrack score={c.score} color={meta.color} />
    </Box>
  );
};

interface FinishedBodyProps {
  competencies: CompetencyRow[];
  highlights: string[];
  delta: string;
  /**
   * Итог по всему ревью: взвешенное среднее и число учтённых оценок. Считается
   * выше по дереву из тех же блоков, что и на экране результатов, — здесь их
   * счётчики уже потеряны при сведении к CompetencyRow.
   */
  total?: { mean: number; count: number } | null;
}

export const FinishedBody: React.FC<FinishedBodyProps> = ({
  competencies,
  highlights,
  delta,
  total,
}) => {
  const { t } = useTranslation();
  const expDefaults = t('perfReview.expectationScale', { returnObjects: true });
  const expLabels = Array.isArray(expDefaults) ? (expDefaults as string[]) : undefined;
  return (
    <Stack gap="md">
      {/* Без оценок карточка была бы пустой рамкой с заголовком. */}
      {(competencies.length > 0 || total) && (
        <Paper withBorder p="xl" radius="md">
          <Flex justify="space-between" align="baseline" mb="md">
            <Eyebrow>{t('perfReview.home.finished.competencies')}</Eyebrow>
            <Badge
              color="teal"
              variant="light"
              size="sm"
              leftSection={<IconTrendingUp size={12} />}
            >
              {delta}
            </Badge>
          </Flex>
          {/* Итог стоит над списком и отделён линией: это вывод по всему ревью,
            а не ещё одна компетенция наравне с прочими. */}
          {total &&
            (() => {
              const meta = scoreExpectation(total.mean, expLabels);
              return (
                <Box
                  mb="lg"
                  pb="md"
                  style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
                >
                  <Flex justify="space-between" align="baseline" gap="sm" mb={8}>
                    <Text size="sm" fw={600}>
                      {t('perfReview.overallScore')}
                    </Text>
                    <Group gap={8} align="baseline" wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Text
                        size="xl"
                        fw={700}
                        c={meta.color}
                        style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}
                      >
                        {total.mean.toFixed(1)}
                      </Text>
                      {meta.label && (
                        <Text size="xs" fw={600} c={meta.color}>
                          {meta.label}
                        </Text>
                      )}
                    </Group>
                  </Flex>
                  <ScoreTrack score={total.mean} color={meta.color} />
                  <Text size="xs" c="dimmed" mt={8}>
                    {t('perfReview.overallScoreHint', { count: total.count })}
                  </Text>
                </Box>
              );
            })()}
          <Stack gap="md">
            {competencies.map((c) => (
              <CompetencyBar key={c.name} c={c} labels={expLabels} />
            ))}
          </Stack>
        </Paper>
      )}
      <Paper withBorder p="xl" radius="md">
        <Eyebrow>{t('perfReview.home.finished.highlights')}</Eyebrow>
        <Stack gap="md" mt="md">
          {highlights.map((h, i) => (
            <Flex key={i} gap="md" align="flex-start">
              <ThemeIcon size={24} radius="md" color="teal" variant="light">
                <NumberMono style={{ fontSize: 11, fontWeight: 600 }}>
                  {String(i + 1).padStart(2, '0')}
                </NumberMono>
              </ThemeIcon>
              <Text size="sm" lh={1.55}>
                {h}
              </Text>
            </Flex>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
};
